<template>
  <div class="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-orange-50 to-red-50">
    <div class="bg-white/90 rounded-2xl shadow-2xl p-8 max-w-lg w-full border border-orange-200/50">
      <h2 class="text-3xl font-bold text-center text-gray-900 mb-6 font-luxury">Register via Invite Token</h2>
      <div v-if="step === 1">
        <label class="block text-lg font-medium text-gray-700 mb-2">Enter your invite token</label>
        <input v-model="token" type="text" placeholder="Paste your invite token here" class="w-full px-4 py-3 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-4" />
        <div class="flex space-x-3">
          <button @click="validateToken" :disabled="loading || !token.trim()" class="flex-1 bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 rounded-lg font-semibold hover:from-orange-700 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
            <span v-if="loading">Validating...</span>
            <span v-else>Validate Token</span>
          </button>
          <button @click="cancelRegistration" class="px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-all duration-200">
            Cancel
          </button>
        </div>
      </div>
      <div v-else-if="step === 2">
        <form @submit.prevent="register" class="space-y-4">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700">Company Name</label>
            <input type="text" :value="invite.company_name" disabled class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100" />
          </div>
          <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p class="text-sm text-blue-800">
              <span class="font-medium">Account Type:</span> 
              <span class="capitalize">{{ invite.role || 'customer' }}</span>
              <span v-if="invite.role === 'customer'"> - Business account with full catalogue access</span>
              <span v-else-if="invite.role === 'admin'"> - Administrative account with management privileges</span>
              <span v-else-if="invite.role === 'staff'"> - Staff account with limited administrative access</span>
              <span v-else-if="invite.role === 'workshop'"> - Workshop account for production management</span>
            </p>
          </div>
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700">Username</label>
            <input v-model="form.username" type="text" required class="w-full px-4 py-3 border border-gray-300 rounded-lg" />
          </div>
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700">Password</label>
            <div class="relative">
              <input v-model="form.password" :type="showPassword ? 'text' : 'password'" required minlength="6" class="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg" />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Toggle password visibility"
              >
                <svg v-if="showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.758 7.758M12 12l2.122 2.122m-2.122-2.122L7.758 7.758m6.364 6.364L12 12m2.122 2.122l2.122 2.122" />
                </svg>
                <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </div>
          </div>
          <!-- Customer-specific fields -->
          <div v-if="invite.role === 'customer'" class="mb-2">
            <label class="block text-sm font-medium text-gray-700">GST Number (Optional)</label>
            <input v-model="form.gst_number" type="text" class="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="22AAAAA0000A1Z5" />
          </div>
          <div v-if="invite.role === 'customer'" class="mb-2">
            <label class="block text-sm font-medium text-gray-700">Contact Person</label>
            <input v-model="form.contact_person" type="text" class="w-full px-4 py-3 border border-gray-300 rounded-lg" />
          </div>
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700">Email</label>
            <input v-model="form.email" type="email" required class="w-full px-4 py-3 border border-gray-300 rounded-lg" />
          </div>
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700">Phone</label>
            <input v-model="form.phone" type="text" required class="w-full px-4 py-3 border border-gray-300 rounded-lg" />
          </div>
          <div v-if="invite.role === 'customer'" class="mb-2">
            <label class="block text-sm font-medium text-gray-700">Address (Optional)</label>
            <textarea v-model="form.address" class="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Enter your business address"></textarea>
          </div>
          <div class="flex space-x-3">
            <button type="submit" :disabled="loading" class="flex-1 bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 rounded-lg font-semibold hover:from-orange-700 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <span v-if="loading">Registering...</span>
              <span v-else>Register</span>
            </button>
            <button @click="cancelRegistration" class="px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-all duration-200">
              Cancel
            </button>
          </div>
        </form>
      </div>
      <div v-else-if="step === 3">
        <div class="text-center">
          <h3 class="text-2xl font-bold text-green-700 mb-4">Registration Successful!</h3>
          <p class="text-gray-700 mb-2">Your credentials have been sent to your email/phone. Please use them to log in.</p>
          <button @click="goToLogin" class="mt-4 px-8 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-semibold hover:from-orange-700 hover:to-red-700 transition-all duration-200">Go to Login</button>
        </div>
      </div>
      <div v-if="error" class="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-center">{{ error }}</div>
    </div>
  </div>
</template>
<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '../stores/notification'
import api from '../services/api'

const step = ref(1)
const token = ref('')
const invite = ref(null)
const form = ref({ username: '', password: '', gst_number: '', contact_person: '', email: '', phone: '', address: '' })
const showPassword = ref(false)
const loading = ref(false)
const error = ref('')
const router = useRouter()
const notification = useNotificationStore()

const validateToken = async () => {
  error.value = ''
  loading.value = true
  try {
    const res = await api.post('/auth/invite/validate', { token: token.value })
    invite.value = res.data.invite
    step.value = 2
    // Prefill email/phone if present
    if (invite.value.email) form.value.email = invite.value.email
    if (invite.value.phone) form.value.phone = invite.value.phone
  } catch (e) {
    error.value = e.response?.data?.message || 'Invalid or expired token.'
    notification.error(error.value)
  } finally {
    loading.value = false
  }
}

const register = async () => {
  error.value = ''
  loading.value = true
  try {
    const payload = { ...form.value, token: token.value }
    await api.post('/auth/invite/register', payload)
    step.value = 3
    notification.success('Registration successful! Credentials sent to your email/phone.')
  } catch (e) {
    error.value = e.response?.data?.message || 'Registration failed.'
    notification.error(error.value)
  } finally {
    loading.value = false
  }
}

const goToLogin = () => {
  router.push('/')
}

const cancelRegistration = () => {
  step.value = 1
  token.value = ''
  invite.value = null
  form.value = { username: '', password: '', gst_number: '', contact_person: '', email: '', phone: '', address: '' }
  error.value = ''
  notification.info('Registration cancelled. Token is still valid for future use.')
}
</script>
<style scoped>
.font-luxury { font-family: 'Playfair Display', serif; }
</style> 